import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WalkInProfessionals } from '@/components/walk-in/WalkInChoices';
import WalkInConfirmation from '@/components/walk-in/WalkInConfirmation';
import { WalkInServiceSelection } from '@/components/walk-in/WalkInServiceSelection';
import { useCompanyBySlug } from '@/hooks/queries/useCompanyBySlug';
import { useCompanyProfessionals } from '@/hooks/queries/useCompanyProfessionals';
import { useCreateWalkInQueue } from '@/hooks/queries/useCreateWalkInQueue';
import { walkInQueryKeys } from '@/hooks/queries/walk-in.queries';
import { useActiveWalkIn } from '@/hooks/queries/useActiveWalkIn';
import { useAuth } from '@/hooks/useAuth';
import { WalkInService } from '@/services/walk-in.service';
import { getApiErrorMessage } from '@/lib/api/error';
import { canCompanyAcceptBookings } from '@/lib/company/status';
import { resolveCompanyAttendanceCapabilities } from '@/lib/company/capabilities';
import type { ProfileWalkInDto } from '@/types/dto/walk-in.dto';

export default function WalkInPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const companyQuery = useCompanyBySlug(slug);
  const company = companyQuery.company;
  const professionalsQuery = useCompanyProfessionals(company?.id, 'QUEUE_ONLY');
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const activeQuery = useActiveWalkIn(isAuthenticated ? user?.id : null);
  const { createWalkInQueue, isPending } = useCreateWalkInQueue();
  const [lastEntry, setLastEntry] = useState<ProfileWalkInDto | null>(null);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [joiningId, setJoiningId] = useState('');
  const submitting = useRef(false);
  const autoAttempt = useRef('');
  const entry = activeQuery.data ?? lastEntry;
  const unavailable =
    company && (!canCompanyAcceptBookings(company) || !resolveCompanyAttendanceCapabilities(company).allowWalkInQueue);

  useEffect(() => {
    if (activeQuery.data) setLastEntry(activeQuery.data);
  }, [activeQuery.data]);

  const join = useCallback(
    async (professionalId: string) => {
      if (submitting.current || !company || !isAuthenticated || !activeQuery.isSuccess || activeQuery.data || lastEntry)
        return;
      submitting.current = true;
      setJoiningId(professionalId);
      setError('');
      try {
        const response = await createWalkInQueue({ companyId: company.id, professionalId, serviceIds: [] });
        const confirmed = {
          ...response,
          companyId: company.id,
          professional: professionalsQuery.professionals.find((person) => person.id === professionalId),
          services: response.services ?? [],
        };
        setLastEntry(confirmed);
        queryClient.setQueryData(walkInQueryKeys.active(user?.id), confirmed);
      } catch (cause) {
        setError(getApiErrorMessage(cause, 'Não conseguimos reservar seu lugar. Tente novamente.'));
        // A lost response may still have reserved a place on the server.
        void activeQuery.refetch();
      } finally {
        submitting.current = false;
        setJoiningId('');
      }
    },
    [
      company,
      isAuthenticated,
      activeQuery,
      lastEntry,
      createWalkInQueue,
      professionalsQuery.professionals,
      queryClient,
      user,
    ],
  );

  useEffect(() => {
    const onlyProfessional = professionalsQuery.professionals.length === 1 ? professionalsQuery.professionals[0] : null;
    if (
      !company ||
      unavailable ||
      professionalsQuery.isLoading ||
      professionalsQuery.isError ||
      !activeQuery.isSuccess ||
      activeQuery.isFetching ||
      entry ||
      !onlyProfessional ||
      !isAuthenticated
    )
      return;
    const key = `${user?.id}:${company.id}:${onlyProfessional.id}`;
    if (autoAttempt.current === key) return;
    autoAttempt.current = key;
    void join(onlyProfessional.id);
  }, [
    company,
    unavailable,
    professionalsQuery,
    activeQuery.isSuccess,
    activeQuery.isFetching,
    entry,
    isAuthenticated,
    user?.id,
    join,
  ]);

  const cancel = async () => {
    if (!entry?.id || cancelling) return;
    setCancelling(true);
    setError('');
    try {
      await WalkInService.cancel(entry.id);
      setLastEntry(entry);
      queryClient.setQueryData(walkInQueryKeys.active(user?.id), null);
      await queryClient.invalidateQueries({ queryKey: walkInQueryKeys.all });
    } catch (cause) {
      setError(getApiErrorMessage(cause, 'Não foi possível sair da fila. Tente novamente.'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-muted/40 min-h-screen pb-12">
      <header className="bg-background border-border border-b">
        <div className="mx-auto flex min-h-16 max-w-5xl items-center gap-3 px-4 py-2 sm:px-5">
          <Button asChild variant="ghost" size="icon" className="h-11 w-11">
            <Link to={`/${slug}`} aria-label="Voltar">
              <ArrowLeft />
            </Link>
          </Button>
          <div className="min-w-0">
            <p className="text-muted-foreground text-xs">Fila presencial</p>
            <p className="text-sm font-semibold">{company?.name ?? 'Carregando local…'}</p>
          </div>
        </div>
      </header>
      <main className={`mx-auto px-4 py-6 sm:px-5 sm:py-8 ${entry ? 'max-w-5xl' : 'max-w-2xl'}`}>
        {companyQuery.isLoading || authLoading || activeQuery.isPending ? (
          <p role="status" className="flex justify-center gap-2 py-12">
            <Loader2 className="animate-spin motion-reduce:animate-none" />
            Consultando sua fila…
          </p>
        ) : !company || companyQuery.isError ? (
          <div role="alert" className="bg-card space-y-4 rounded-2xl border p-5">
            <h1 className="text-xl font-bold">Não conseguimos carregar este local</h1>
            <Button onClick={() => void companyQuery.refetch()}>Tentar novamente</Button>
          </div>
        ) : entry ? (
          entry.companyId !== company.id ? (
            <div className="space-y-4">
              <h1 className="text-xl font-bold">Você já está em outra fila</h1>
              <Button asChild>
                <Link to="/perfil?tab=bookings">Ver minha fila</Link>
              </Button>
            </div>
          ) : (
            <WalkInConfirmation
              companyName={company.name}
              companySlug={slug}
              entry={entry}
              ended={activeQuery.data === null}
              updating={activeQuery.isFetching}
              error={activeQuery.isError}
              onRefresh={() => void activeQuery.refetch()}
              onCancel={cancel}
              cancelling={cancelling}
            >
              {activeQuery.data !== null && ['WAITING', 'CALLED'].includes(entry.status ?? '') && entry.id && (
                <WalkInServiceSelection
                  key={entry.id}
                  entry={entry}
                  companyId={company.id}
                  allowMultiple={company.allowMultipleServicesPerBooking !== false}
                />
              )}
            </WalkInConfirmation>
          )
        ) : unavailable ? (
          <h1 className="text-xl font-bold">A fila não está disponível neste local</h1>
        ) : activeQuery.isError ? (
          <div role="alert" className="bg-card space-y-4 rounded-2xl border p-5">
            <p>Não foi possível verificar se você já está na fila.</p>
            <Button onClick={() => void activeQuery.refetch()}>Tentar novamente</Button>
          </div>
        ) : professionalsQuery.isError ? (
          <div role="alert" className="bg-card space-y-4 rounded-2xl border p-5">
            <p>Não foi possível carregar os profissionais.</p>
            <Button onClick={() => void professionalsQuery.refetch()}>Tentar novamente</Button>
          </div>
        ) : professionalsQuery.isLoading || joiningId || isPending ? (
          <p role="status" className="flex justify-center gap-2 py-12">
            <Loader2 className="animate-spin motion-reduce:animate-none" />
            {joiningId || isPending ? 'Reservando seu lugar…' : 'Carregando profissionais…'}
          </p>
        ) : (
          <div className="space-y-6">
            <div className="border-primary/20 bg-primary-light text-primary flex items-center gap-3 rounded-xl border p-4 text-sm">
              <span className="bg-primary text-primary-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-semibold">
                1
              </span>
              <p>
                Escolha o profissional <span className="text-muted-foreground">→ Acompanhe sua vez</span>
              </p>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Com quem você quer ser atendido?</h1>
              <p className="text-muted-foreground mt-2 text-sm">
                Ao escolher, você já entra na fila desse profissional. Os serviços podem ser escolhidos depois.
              </p>
            </div>
            <WalkInProfessionals
              professionals={professionalsQuery.professionals}
              professionalId=""
              onSelect={(id) => void join(id)}
            />
            <p className="text-muted-foreground text-sm leading-relaxed">
              Atendimento por ordem da fila, sem horário marcado. Aguarde no estabelecimento para ser chamado.
            </p>
          </div>
        )}
        {error && (
          <p role="alert" className="text-destructive mt-4 text-sm">
            {error}
          </p>
        )}
      </main>
    </div>
  );
}
